import React, { useState, useEffect } from "react";
import Pagination from "../common/Pagination";
import NavBar from "../common/NavBar";
import Footer from "../common/Footer";
import OwnerMenu from "./OwnerMenu"; // Import the OwnerMenu component
import { useTranslation } from "react-i18next";

const Locations = () => {
  const { t } = useTranslation();
  const [locations, setLocations] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locationData, setLocationData] = useState({
    name: "",
    country: "",
    state: "",
    latitude: "",
    longitude: "",
  });
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [locationIdToUpdate, setLocationIdToUpdate] = useState(null);
  const [userGroup, setUserGroup] = useState(null); // State to track the user's group
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 5;

  // Determine the API base URL based on the environment
  const getApiBaseUrl = () => {
    // Use the REACT_APP_API_URL from .env, fallback to localhost if not set
    return process.env.REACT_APP_API_URL || "http://localhost:8000";
  };

  // Fetch the user's group from localStorage
  useEffect(() => {
    const rawGroups = localStorage.getItem("groups");
    let storedGroups = [];

    if (rawGroups) {
      try {
        const parsed = JSON.parse(rawGroups);
        storedGroups = Array.isArray(parsed) ? parsed : [parsed];
      } catch {
        storedGroups = [rawGroups];
      }
    }

    // Check if the user belongs to the "Owner" group
    const isOwner = storedGroups.some((group) => String(group).trim() === "Owners");
    if (isOwner) {
      setUserGroup("Owner");
    }
  }, []);

  // Fetch all locations
  useEffect(() => {
    fetch(`${getApiBaseUrl()}/api/locations/`)
      .then((response) => response.json())
      .then((data) => setLocations(data))
      .catch((error) => console.error("Error fetching locations:", error));
  }, []);

  // Handle form submission for creating/updating a location
  const handleSubmit = async (e) => {
    e.preventDefault();

    const endpoint = isUpdateMode
      ? `${getApiBaseUrl()}/api/locations/${locationIdToUpdate}/`
      : `${getApiBaseUrl()}/api/locations/`;

    const method = isUpdateMode ? "PUT" : "POST";

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(locationData),
      });

      if (!response.ok) {
        throw new Error(t("Failed create or update location"));
      }

      alert(t("Location Created or Updated Successfully"));
      setIsModalOpen(false);
      window.location.reload();
    } catch (error) {
      console.error("Error creating/updating location:", error);
      alert(t("Failed to create or update location, please try again"));
    }
  };

  // Handle delete location
  const handleDelete = async (locationId) => {
    if (!window.confirm(t("Are you sure to delete this location"))) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/locations/${locationId}/`, {
        method: "DELETE",
      });

      if (response.ok) {
        setLocations((prev) => prev.filter((loc) => loc.id !== locationId));
        alert(t("Location deleted successfully"));
      } else {
        alert(t("Error deleting location"));
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      alert(t("error_occurred_try_again"));
    }
  };

  // Open the modal for creating a new location
  const openCreateModal = () => {
    setIsUpdateMode(false);
    setLocationIdToUpdate(null);
    setLocationData({
      name: "",
      country: "",
      state: "",
      latitude: "",
      longitude: "",
    });
    setIsModalOpen(true);
  };

  // Open the modal for updating an existing location
  const openUpdateModal = (location) => {
    setIsUpdateMode(true);
    setLocationIdToUpdate(location.id);
    setLocationData({
      name: location.name,
      country: location.country,
      state: location.state,
      latitude: location.latitude,
      longitude: location.longitude,
    });
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />

      {/* Show OwnerMenu if the user is in the "Owner" group */}
      {userGroup === "Owner" && <OwnerMenu />}

      <div className="flex flex-col items-center mt-16">
        <h1 className="text-3xl font-bold mb-8">{t("locations")}</h1>

        <button
          onClick={openCreateModal}
          className="bg-blue-500 text-white px-4 py-2 rounded-md mb-4 hover:bg-blue-700"
        >
          {t("Create Location")}
        </button>

        <div className="w-full max-w-4xl overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-100 text-center">
                <th className="py-3 px-4 border-b">{t("name")}</th>
                <th className="py-3 px-4 border-b">{t("country")}</th>
                <th className="py-3 px-4 border-b">{t("state")}</th>
                <th className="py-3 px-4 border-b">{t("latitude")}</th>
                <th className="py-3 px-4 border-b">{t("longitude")}</th>
                <th className="py-3 px-4 border-b">{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {locations.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((location) => (
                <tr key={location.id} className="hover:bg-gray-50 text-center">
                  <td className="py-3 px-4 border-b">{location.name}</td>
                  <td className="py-3 px-4 border-b">{location.country}</td>
                  <td className="py-3 px-4 border-b">{location.state}</td>
                  <td className="py-3 px-4 border-b">{location.latitude}</td>
                  <td className="py-3 px-4 border-b">{location.longitude}</td>
                  <td className="py-3 px-4 border-b flex justify-center space-x-2">
                    <button
                      onClick={() => openUpdateModal(location)}
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-700"
                    >
                      {t("update")}
                    </button>
                    <button
                      onClick={() => handleDelete(location.id)}
                      className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-700"
                    >
                      {t("delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination totalItems={locations.length} pageSize={PAGE_SIZE} currentPage={currentPage} onPageChange={setCurrentPage} />
      </div>

      {/* Modal for creating/updating locations */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {isUpdateMode ? t("update_location") : t("add_location")}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700">{t("name")}</label>
                <input
                  type="text"
                  value={locationData.name}
                  onChange={(e) => setLocationData({ ...locationData, name: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">{t("country")}</label>
                <input
                  type="text"
                  value={locationData.country}
                  onChange={(e) => setLocationData({ ...locationData, country: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">{t("state")}</label>
                <input
                  type="text"
                  value={locationData.state}
                  onChange={(e) => setLocationData({ ...locationData, state: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">{t("latitude")}</label>
                <input
                  type="number"
                  step="0.0001"
                  value={locationData.latitude}
                  onChange={(e) => setLocationData({ ...locationData, latitude: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">{t("longitude")}</label>
                <input
                  type="number"
                  step="0.0001"
                  value={locationData.longitude}
                  onChange={(e) => setLocationData({ ...locationData, longitude: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md mr-2 hover:bg-gray-600"
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
                >
                  {isUpdateMode ? t("update") : t("add")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Locations;